const PERSON_NAME_PATTERN = /^[\p{L}\s.-]+$/u;
export const REQUIRED_PERSON_NAME_MIN_LENGTH = 2;
const LETTERS_ONLY_PATTERN = /^[\p{L}\s-]+$/u;
const ADDRESS_LINE_PATTERN = /^[\p{L}\d/,\s.-]+$/u;
const SIGNING_TIME_PATTERN = /^\d{1,2}:\d{2}$/;
const DOCUMENT_NUMBER_PATTERN = /^[\da-z]{1,10}$/i;
const ALLOWED_CURRENCIES = new Set(['CZK', 'EUR', 'USD']);
const NON_NEGATIVE_NUMBER_PATTERN = /^\d+(?:[.,]\d+)?$/;
const PHONE_NUMBER_CHARACTERS_PATTERN = /^\+?[\d\s\-()]+$/;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.includes(' ')) {
    return false;
  }
  const parts = trimmed.split('@');
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return false;
  }
  const domain = parts[1];
  if (!domain || domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) {
    return false;
  }
  const lastDotIndex = domain.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return false;
  }
  const tld = domain.slice(lastDotIndex + 1);
  return tld.length >= 2;
}

export function isValidPhoneDigits(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || !PHONE_NUMBER_CHARACTERS_PATTERN.test(trimmed)) {
    return false;
  }
  const digits = trimmed.replaceAll(/\D/g, '');
  return digits.length >= 6 && digits.length <= 15;
}

export function isValidPhonePrefix(value: string): boolean {
  return /^\+?\d{1,4}$/.test(value.trim());
}

export function isValidPostalCode(value: string): boolean {
  const digits = value.replaceAll(/\s/g, '');
  if (!/^\d+$/.test(digits)) {
    return false;
  }
  if (digits.length === 5) {
    return true;
  }
  return digits.length >= 4 && digits.length <= 6;
}

export function isValidDateDdMmYyyy(value: string): boolean {
  const parts = value.trim().split('.');
  if (parts.length !== 3) {
    return false;
  }
  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return false;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export function isValidBirthDateDdMmYyyy(value: string): boolean {
  if (!isValidDateDdMmYyyy(value)) {
    return false;
  }
  const yearPart = value.trim().split('.')[2] ?? '';
  return yearPart.startsWith('19') || yearPart.startsWith('20');
}

export function isValidSigningTime(value: string): boolean {
  const trimmed = value.trim();
  if (!SIGNING_TIME_PATTERN.test(trimmed)) {
    return false;
  }
  const [hoursPart, minutesPart] = trimmed.split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

export function isValidVerificationCode(value: string): boolean {
  return /^\d{6}$/.test(value.trim());
}

export function isValidBirthNumber(value: string): boolean {
  const normalized = value.replaceAll(/\s/g, '');
  return /^\d{9}$/.test(normalized) || /^\d{6}\/?\d{3,4}$/.test(normalized);
}

export function isValidIco(value: string): boolean {
  const digits = value.replaceAll(/\s/g, '');
  if (!/^\d{8}$/.test(digits)) {
    return false;
  }
  const weights = [8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let index = 0; index < 7; index += 1) {
    sum += Number(digits[index]) * weights[index]!;
  }
  const remainder = sum % 11;
  let checkDigit = 11 - remainder;
  if (checkDigit === 10) {
    checkDigit = 0;
  }
  if (checkDigit === 11) {
    checkDigit = 1;
  }
  return Number(digits[7]) === checkDigit;
}

export function isValidPartnerNumber(value: string): boolean {
  return /^\d{6}$/.test(value.replaceAll(/\s/g, ''));
}

export function isValidDocumentNumber(value: string): boolean {
  return DOCUMENT_NUMBER_PATTERN.test(value.trim());
}

export function isValidAddressLine(value: string): boolean {
  return ADDRESS_LINE_PATTERN.test(value.trim());
}

export function isValidLettersOnly(value: string): boolean {
  return LETTERS_ONLY_PATTERN.test(value.trim());
}

export function isValidPersonName(value: string, minLength = 1): boolean {
  const trimmed = value.trim();
  if (!PERSON_NAME_PATTERN.test(trimmed)) {
    return false;
  }
  return trimmed.length >= minLength;
}

export function isValidAllowedCurrency(value: string): boolean {
  return ALLOWED_CURRENCIES.has(value.trim());
}

export function isValidNonNegativeNumber(value: string): boolean {
  const trimmed = value.trim();
  if (!NON_NEGATIVE_NUMBER_PATTERN.test(trimmed)) {
    return false;
  }
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0;
}
